import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, setHeaderMenuStyle } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The header's resting box, measured once. Everything about showing and hiding is decided
   * by comparing the scroll position against it.
   * @type {DOMRectReadOnly | null}
   */
  #headerBounds = null;

  /**
   * Document-space y of the header's top edge at rest. Usually 0, but not when something
   * sits above it in the header group.
   * @type {number}
   */
  #headerTop = 0;

  /**
   * Document-space y of the header's bottom edge at rest.
   * @type {number}
   */
  #headerBottom = 0;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      if (!alwaysSticky) {
        // Measure once and stop watching. The scroll-up header used to keep this observer
        // running and decide everything from whether the header was on screen — which
        // cannot work once the header is pinned in both its shown and hidden states,
        // because then it never leaves the screen and the reveal never fires again.
        // Its resting position is all we need; #updateScrollState compares against it.
        this.#headerBounds = entry.boundingClientRect;
        this.#headerTop = (window.pageYOffset || 0) + entry.boundingClientRect.top;
        this.#headerBottom = (window.pageYOffset || 0) + entry.boundingClientRect.bottom;
        observer.disconnect();
        this.#intersectionObserver = null;
        return;
      }

      if (this.#stickyStateFrozen()) return;

      this.dataset.stickyState = entry.isIntersecting ? 'inactive' : 'active';
      if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
    }, config);

    const observer = this.#intersectionObserver;
    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  /**
   * Whether the mobile menu drawer is currently open.
   * @returns {boolean}
   */
  #menuDrawerOpen = () => document.getElementById('Details-menu-drawer-container')?.open === true;

  /**
   * The sticky state the header had when the drawer opened, put back when it closes.
   * @type {string | null}
   */
  #stickyStateBeforeDrawer = null;

  /**
   * Timestamp until which scroll updates are ignored after the drawer closes.
   * @type {number}
   */
  #drawerSettleUntil = 0;

  /**
   * On a transparent header the whole look — text colour, underlay height, logo variant —
   * hangs off data-sticky-state, so any flip of it while the menu is over the page shows
   * up as the bar changing appearance. Opening the drawer scroll-locks the document and
   * closing it releases the lock, and both fire scroll events with a scrollTop that need
   * not match the one we left off at. Freezing the state across the whole interaction is
   * the only way to guarantee the header looks identical before and after.
   * @param {Event} event
   */
  #handleDrawerToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || details.id !== 'Details-menu-drawer-container') return;

    if (details.open) {
      this.#stickyStateBeforeDrawer = this.dataset.stickyState ?? null;
      return;
    }

    // Resync before restoring, so the scroll event the unlock is about to fire compares
    // against where the page actually is and reads as no movement. The settle window
    // covers the frames where the browser is still putting the scroll position back.
    this.#lastScrollTop = document.scrollingElement?.scrollTop ?? this.#lastScrollTop;
    if (this.#stickyStateBeforeDrawer !== null) this.dataset.stickyState = this.#stickyStateBeforeDrawer;
    this.#stickyStateBeforeDrawer = null;
    this.#drawerSettleUntil = performance.now() + 400;
  };

  /**
   * Whether the header's sticky state is currently frozen for the menu drawer.
   * @returns {boolean}
   */
  #stickyStateFrozen = () => this.#menuDrawerOpen() || performance.now() < this.#drawerSettleUntil;

  #updateScrollState = () => {
    // Nothing about the header may change while the menu is over it, nor in the frames
    // right after it closes. The scroll-lock and its release both fire scroll events with
    // a scrollTop that need not match the one we left off at, and on a transparent header
    // a single flip of the sticky state repaints the whole bar. While the drawer is open
    // we ignore the reading entirely; while it settles we keep up with it, so that once
    // the window passes the next real scroll compares against the truth.
    if (this.#menuDrawerOpen()) return;
    if (performance.now() < this.#drawerSettleUntil) {
      this.#lastScrollTop = document.scrollingElement?.scrollTop ?? this.#lastScrollTop;
      return;
    }

    const stickyMode = this.getAttribute('sticky');
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;

    // No actual scroll movement must not change the sticky state. Releasing a drawer's
    // scroll-lock (e.g. closing the mobile menu) fires a no-movement scroll event, which
    // would otherwise flip the header and hide it even though the user hasn't scrolled.
    if (scrollTop === this.#lastScrollTop) return;

    const isScrollingUp = scrollTop < this.#lastScrollTop;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (this.getBoundingClientRect().top >= 0) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    // While the header is still in the flow, its document-space box is worth re-deriving
    // on every scroll: getBoundingClientRect() falls by exactly what pageYOffset gains, so
    // the sums are constant and cost nothing to keep honest. It also covers the first
    // scroll, which can land before the observer above has measured, and any resize that
    // changes the header's height while you are near the top.
    if (!this.#offscreen) {
      const rect = this.getBoundingClientRect();
      const scrollY = window.pageYOffset || 0;
      this.#headerTop = scrollY + rect.top;
      this.#headerBottom = scrollY + rect.bottom;
    }

    // Three zones, not two, and the middle one is the point.
    //
    // Past the header's bottom, it is pinned and the scroll direction alone decides shown
    // or hidden. At or above its own top, it is handed back to the flow. In between —
    // the band its own height covers — nothing changes at all.
    //
    // That middle band is what makes the return seamless. Un-pinning anywhere inside it
    // means the bar leaves the top of the screen and snaps to its document position, which
    // is still above the viewport: on desktop it jumps, on mobile it vanishes and waits at
    // the top. Holding the state until the page is genuinely at the top means the pinned
    // position and the resting position are the same place by the time they swap, and
    // nothing moves.
    //
    // Everything here is measured against the page, never against where the header is now:
    // it stays pinned in both states so its `top` can animate, which leaves its current
    // position saying nothing about how far you have scrolled.
    if (scrollTop > this.#headerBottom) {
      this.#offscreen = true;

      if (isScrollingUp) {
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.stickyState = 'idle';
        this.dataset.scrollDirection = 'none';
      }
    } else if (scrollTop <= this.#headerTop) {
      this.#offscreen = false;
      this.dataset.stickyState = 'inactive';
      this.dataset.scrollDirection = 'none';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      // Capture (third argument): the toggle event does not bubble.
      document.addEventListener('toggle', this.#handleDrawerToggle, true);

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.removeEventListener('toggle', this.#handleDrawerToggle, true);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
